/**
 * /web/folder/get + /web/folder/statistic 返回 → FolderListItem 字段适配
 *
 * 关注点：
 *  · 隔离后端字段命名（folderID / folderName / createdAt / isIntelligenceFolder ...）
 *    与前端类型 FolderListItem 之间的差异
 *  · 把缺失字段填默认值，调用侧无需再判空
 *  · 新增字段（如 createdAt / isIntelligenceFolder）只改本文件
 *
 * 位置：归在 slices/aiClassify 数据层（与 folderListSlice 同级）· 列表 / 详情页
 * 共用。原先放在 pages/AIClassifyFolder/components/FolderList 下，列表数据源
 * 提升为 slice 后，为避免「slice 反向依赖 page」的层级倒置，下移到数据层。
 */

import type { FolderListItem } from '../../types/ai-classify'

/**
 * 后端 /web/folder/get 返回的单条原始 folder · 仅声明本组件用得到的字段
 *
 * folderID 实际后端返回的是 number 而非 string（与旧 photoFolders 代码一致），
 * 在 adapter 里统一 String 化为 FolderListItem.folderId，下游可放心当字符串用。
 */
interface RawFolder {
  folderID: string | number
  folderName: string
  folderType?: number
  /**
   * 创建时间 · 后端字段 createdAt（2026-05-08 起接口下发）
   *  · 单位秒级 · normalizeTimestamp 兜底处理
   *  · ctime / createTime 为历史 fallback 命名（部分老接口）
   */
  createdAt?: number
  ctime?: number
  createTime?: number
  /**
   * 是否 AI 智能分类文件夹 · 后端字段 isIntelligenceFolder（2026-05-08 起下发）
   *  · 替代之前按 folderId 哈希 mock 的临时方案
   */
  isIntelligenceFolder?: boolean
  dimensionGroupID?: string | number
  dimensionType?: string
  dimensionQualifier?: string
  dimensionDisplayName?: string
  dimensionValueKey?: string
  /**
   * 最后更新时间 · 后端字段名候选（不同接口历史命名不一）
   *
   * 主用 folderUpdateTime —— 与 photoFolders/components/FoldersTable 现有列对齐,
   * 单位是秒；normalizeTimestamp 会把 < 1e12 的值乘 1000 换成毫秒。
   * utime / updateTime / mtime 是历史 fallback，万一某些接口不返 folderUpdateTime
   * 时不至于全空。
   */
  folderUpdateTime?: number
  utime?: number
  updateTime?: number
  mtime?: number
}

/** /web/folder/statistic 返回的单条统计 · 仅声明用得到的字段 */
interface RawFolderStatistic {
  folderID: string | number
  photoCount?: number
  folderCount?: number
}

/**
 * 把后端时间戳归一为毫秒
 *
 * 兼容秒级（< 1e12 视为秒）和毫秒级两种实现。
 */
function normalizeTimestamp(value: number | undefined): number {
  if (!value || value <= 0) return 0
  return value < 1e12 ? value * 1000 : value
}

/**
 * 合并 folder 原始数据 + 对应的 statistic（可缺）→ FolderListItem
 *
 * 缺失字段策略：
 *  · folderType: 默认 0（普通文件夹）
 *  · photoCount: statistic 缺失或为 0 时给 0
 *  · subfolderCount: statistic.folderCount，缺失给 0
 *  · ctime: 优先 createdAt（新字段）→ ctime / createTime（fallback）→ 0
 *  · aiGenerated: 优先 isIntelligenceFolder（新字段）· 缺时为 false（视为普通）
 */
export function adaptFolder(
  raw: RawFolder,
  statistic?: RawFolderStatistic
): FolderListItem {
  const folderId = String(raw.folderID)
  return {
    folderId,
    folderName: raw.folderName,
    folderType: raw.folderType ?? 0,
    photoCount: statistic?.photoCount ?? 0,
    subfolderCount: statistic?.folderCount ?? 0,
    // 优先用新字段 createdAt · ctime / createTime 仅作历史接口 fallback
    ctime: normalizeTimestamp(raw.createdAt ?? raw.ctime ?? raw.createTime),
    // 多个候选字段名 · 优先 folderUpdateTime（与 photoFolders 现有列对齐），
    // 退回历史命名；都缺时为 0，列表视图显示"—"
    updateTime: normalizeTimestamp(
      raw.folderUpdateTime ?? raw.utime ?? raw.updateTime ?? raw.mtime ?? 0
    ),
    aiGenerated: raw.isIntelligenceFolder === true,
    dimensionGroupId:
      raw.dimensionGroupID === undefined || raw.dimensionGroupID === null
        ? undefined
        : String(raw.dimensionGroupID),
    dimensionType: raw.dimensionType || undefined,
    dimensionQualifier: raw.dimensionQualifier || undefined,
    dimensionDisplayName: raw.dimensionDisplayName || undefined,
    dimensionValueKey: raw.dimensionValueKey || undefined
  }
}

/**
 * 批量合并 · 按 folderID 把 statistic 数组拍平到 folder 列表
 *
 * statistic 未到时仍会返回 photoCount=0 的列表，避免 UI 等待。
 */
export function adaptFolderList(
  rawFolders: RawFolder[],
  rawStatistics: RawFolderStatistic[] = []
): FolderListItem[] {
  // 用 Map 加速查找，避免双重 O(n²) 扫描
  // 用 String 化 key 避免 number / string 类型混用导致 lookup miss
  const statMap = new Map<string, RawFolderStatistic>()
  for (const s of rawStatistics) {
    statMap.set(String(s.folderID), s)
  }
  return rawFolders.map((f) => adaptFolder(f, statMap.get(String(f.folderID))))
}
